import React, { ChangeEvent, ReactElement, useState } from 'react'
import { onlyString } from '../helpers/dataTransformer'
import { hasForbiddenChars, maxCharsDisplay } from '../helpers/fieldHelper'
import FieldStyles, { FieldSize } from '../components/FieldStyles'

export enum InputType {
  Text = 'text',
  Email = 'email',
  Password = 'password',
  Phone = 'tel',
  Number = 'number',
}
export type InputFieldProps = {
  name?: string
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  className?: string
  isError?: boolean
  type?: InputType
  label?: string
  isLong?: boolean
  disabled?: boolean
  icon?: React.SVGProps<SVGElement>
  maxChars?: number
  hideMaxChars?: boolean
  subtitle?: string
  style?: object
  autoComplete?:
  | 'given-name'
  | 'family-name'
  | 'email'
  | 'address-line1'
  | 'address-line2'
  | 'address-line3'
  | 'postal-code'
  fieldSize?: FieldSize
}

function InputField(props: InputFieldProps): ReactElement {
  const {
    type = InputType.Text,
    name,
    placeholder,
    isError,
    value,
    onChange,
    label,
    isLong,
    disabled,
    className,
    icon,
    maxChars,
    hideMaxChars,
    subtitle,
    style,
    autoComplete,
    fieldSize,
  } = props
  const [contentError, setContentError] = useState('')

  const errorClass = isError || contentError ? 'error' : ''
  const longClass = isLong ? 'input-long' : ''
  const classNames = `styled-input input-styles ${errorClass} ${longClass} ${className}`

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const existingValue = value && typeof value === 'string' ? value : ''
    const newValue = event.target.value

    if (hasForbiddenChars(newValue)) {
      setContentError('Character not allowed')
      return
    }
    // if we are over max characters AND we are not trying to reduce character count
    if (
      maxChars
      && newValue.length > maxChars
      && newValue.length > existingValue.length
    ) {
      setContentError('Over character limit')
      return
    }
    setContentError('')

    // if we are working with numbers, don't allow letters
    if (
      (type === InputType.Phone || type === InputType.Number)
      && /[A-Za-z]/g.test(newValue)
    ) {
      return
    }

    if (onChange) onChange(newValue)
  }

  return (
    <FieldStyles
      label={label}
      maxChars={maxCharsDisplay(value?.toString().length, maxChars)}
      hideMaxChars={hideMaxChars}
      subtitle={subtitle}
      fieldSize={fieldSize}
      error={contentError}
    >
      <div style={{ position: 'relative' }}>
        <input
          id={label ? onlyString(label) : undefined}
          type={type === InputType.Number ? 'text' : type}
          name={name}
          placeholder={placeholder}
          className={classNames}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          style={style}
          autoComplete={autoComplete}
        />
        <div className="input-icon">{icon}</div>
      </div>
    </FieldStyles>
  )
}

export default InputField
